import { GetProjectEstimatesQuery } from '@/gql/graphql';
import usePreferredNumberFormat from '@/hooks/usePreferredNumberFormat';
import EstimateLastViewedIndicator from '@/ui/components/estimateLastViewedIndicator/EstimateLastViewedIndicator';
import EstimateStatusBadge from '@/ui/components/estimateStatusBadge/EstimateStatusBadge';
import { AnchorButton, Card, H3, H4, Text, Tooltip } from '@payaca/ambrosia';
import CurrencyValue from '@payaca/components/currencyValue/CurrencyValue';
import UntitledIcon from '@payaca/untitled-icons';
import { format } from 'date-fns';
import { FC } from 'react';
import { Link } from 'react-router-dom';
import BillSavingsSection from './BillSavingsSection';
import OutOfDateHeatLossWarning from './OutOfDateHeatLossWarning';
import OutOfDateSolarRoofWarning from './OutOfDateSolarRoofWarning';

type Estimate = GetProjectEstimatesQuery['projectById']['estimates'][number];

type EstimateCardProps = {
  projectId: string;
  estimate: Estimate;
};

const EstimateCard: FC<EstimateCardProps> = (props) => {
  const { projectId, estimate } = props;

  const showHeatLossOutOfDateWarning =
    estimate.status !== 'ARCHIVED' && estimate.isHeatLossOutOfDate;
  const showSolarRoofOutOfDateWarning =
    estimate.status !== 'ARCHIVED' && estimate.isSolarRoofOutOfDate;
  const solarSystem = estimate.solarEstimate?.systemSummary;
  const sourceDesign = estimate.solarEstimate?.sourceDesign;
  const solarNumber = usePreferredNumberFormat({ maximumFractionDigits: 2 });
  const solarSystemDetails = [
    solarSystem?.panelCount != null
      ? `${solarNumber.format(solarSystem.panelCount)} ${solarSystem.panelCount === 1 ? 'panel' : 'panels'}`
      : null,
    solarSystem?.systemSizeKw != null
      ? `${solarNumber.format(solarSystem.systemSizeKw)} kWp`
      : null,
    solarSystem?.batteryCapacityKwh != null &&
    solarSystem.batteryCapacityKwh > 0
      ? `${solarNumber.format(solarSystem.batteryCapacityKwh)} kWh battery`
      : null,
  ].filter((detail): detail is string => detail != null);

  return (
    <Card>
      <Card.Body className="space-y-4">
        <div className="flex flex-col items-stretch gap-3 border-b pb-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap items-center gap-3">
            <H3>{`Estimate ${estimate.reference}`}</H3>
            {estimate.categories.includes('HEAT_PUMP') && (
              <div className="flex items-center gap-1.5">
                <div
                  aria-hidden="true"
                  className="flex size-6 shrink-0 items-center justify-center rounded bg-cyan-50 text-cyan-600"
                >
                  <UntitledIcon name="thermometer-02.3" className="size-4" />
                </div>
                <Text as="span" level="supporting">
                  Flow
                </Text>
              </div>
            )}
            {estimate.categories.includes('SOLAR') && (
              <div className="flex items-center gap-1.5">
                <div
                  aria-hidden="true"
                  className="flex size-6 shrink-0 items-center justify-center rounded bg-yellow-50 text-yellow-600"
                >
                  <UntitledIcon name="sun.3" className="size-4" />
                </div>
                <Text as="span" level="supporting">
                  Glow
                </Text>
              </div>
            )}
          </div>
          <div className="flex flex-wrap items-center justify-end gap-3 sm:flex-nowrap">
            {estimate.archivedAt && (
              <div className="flex flex-col items-end">
                <Text as="div" level="supporting">
                  Archived
                </Text>
                <Text as="div" level="supporting">
                  {format(new Date(estimate.archivedAt), "d MMM ''yy")}
                </Text>
              </div>
            )}
            {showHeatLossOutOfDateWarning && <OutOfDateHeatLossWarning />}
            {showSolarRoofOutOfDateWarning && <OutOfDateSolarRoofWarning />}
            <EstimateLastViewedIndicator lastViewedAt={estimate.lastViewedAt} />
            <EstimateStatusBadge
              status={estimate.status}
              sentAt={estimate.sentAt}
              bouncedAt={estimate.bouncedAt}
            />
            <AnchorButton
              as={Link}
              to={`/projects/${projectId}/estimates/${estimate.id}`}
              variant="secondary"
              size="sm"
              className="shrink-0 whitespace-nowrap"
              aria-label={`View details for estimate ${estimate.reference}`}
            >
              View details
            </AnchorButton>
          </div>
        </div>
        {(solarSystemDetails.length > 0 || sourceDesign) && (
          <div className="space-y-1">
            <H4>Solar system</H4>
            {solarSystemDetails.length > 0 && (
              <Text>{solarSystemDetails.join(' · ')}</Text>
            )}
            {sourceDesign && (
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                <Text level="supporting">
                  From {sourceDesign.name} · revision {sourceDesign.revision}
                </Text>
                <Tooltip tooltipContent="Opens the current editable draft">
                  <AnchorButton
                    as={Link}
                    to={`/projects/${projectId}/solar-designer?design=${sourceDesign.publicId}`}
                    variant="link"
                    size="sm"
                    aria-label={`Open source design: ${sourceDesign.name}`}
                  >
                    Open source design
                  </AnchorButton>
                </Tooltip>
              </div>
            )}
          </div>
        )}
        <div className="flex flex-col-reverse gap-4 md:flex-row md:gap-6">
          <div className="max-w-3xl flex-1 space-y-3">
            {estimate.grants.length === 0 ? (
              <div className="flex items-center justify-between">
                <H4>Total</H4>
                <H4>
                  <CurrencyValue {...estimate.totalIncTax} />
                </H4>
              </div>
            ) : (
              <div className="grid grid-cols-2 items-end gap-y-1.5">
                <Text>Total (including tax)</Text>
                <div className="text-right">
                  <Text as="span">
                    <CurrencyValue {...estimate.totalIncTax} />
                  </Text>
                </div>
                {estimate.grants.map((grant) => (
                  <GrantDeductionRow key={grant.id} grant={grant} />
                ))}
                <div className="border-t pt-1.5">
                  <H4>Total payable</H4>
                </div>
                <div className="border-t pt-1.5 text-right">
                  <H4>
                    <CurrencyValue {...estimate.totalPayable} />
                  </H4>
                </div>
              </div>
            )}
          </div>
          {estimate.billSavingsEstimate && (
            <div className="flex-1 border-b pb-4 md:border-b-0 md:border-l md:pb-0 md:pl-6">
              <BillSavingsSection
                billSavingsEstimate={estimate.billSavingsEstimate}
              />
            </div>
          )}
        </div>
      </Card.Body>
    </Card>
  );
};

type GrantDeductionRowProps = {
  grant: Estimate['grants'][number];
};

const GrantDeductionRow: FC<GrantDeductionRowProps> = (props) => {
  const { grant } = props;
  return (
    <>
      <Text>{grant.name}</Text>
      <div className="text-right">
        <Text as="span">
          −<CurrencyValue {...grant.amount} />
        </Text>
      </div>
    </>
  );
};

export default EstimateCard;
